"use client";

import { useState, useCallback, useRef, useEffect, useId } from "react";
import ChatScreen from "@/components/ChatScreen";
import { useFocusTrap } from "@/lib/useFocusTrap";

type SheetState = "half" | "full";

const SCREEN_PLACEHOLDERS: Record<string, string> = {
  home: "What's on your mind?",
  schedule: "Need help with your plan?",
  goals: "How are your goals going?",
  progress: "Want to explore your patterns?",
  queue: "What should you watch or read next?",
  settings: "Any questions about your setup?",
};

interface Props {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  screenContext?: string;
  initialMessage?: string | null;
  onInitialMessageConsumed?: () => void;
  /** WIP-1 § 8-7 U5: id of the goal the user has in focus when chat is
   *  opened from a goal-context surface. Forwarded to ChatScreen and only
   *  attached to chat requests when screenContext === "goals". */
  focusedGoalId?: string | null;
  /**
   * Hide the floating Chat trigger when another overlay (Settings,
   * NewDecision, etc.) is open on top of it. Without this, the
   * mobile floating button visually layers over the active sheet —
   * the audit explicitly flagged this as the worst remaining mobile
   * layering issue.
   */
  triggerHidden?: boolean;
}

export default function ChatSheet({
  open,
  onOpen,
  onClose,
  screenContext = "home",
  initialMessage,
  onInitialMessageConsumed,
  focusedGoalId = null,
  triggerHidden = false,
}: Props) {
  const [state, setState] = useState<SheetState>("half");
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const isOpen = open;

  useEffect(() => {
    if (open) {
      setState("half");
    }
  }, [open]);

  const toggleFull = useCallback(() => {
    setState((s) => (s === "full" ? "half" : "full"));
  }, []);

  // Lock body scroll while the sheet is open so the page underneath
  // doesn't scroll under it on mobile.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Trap Tab/Shift+Tab cycling inside the sheet, fire onClose on
  // Esc, and restore focus to whichever button opened the chat when
  // it closes. Initial focus lands on the chat textarea so the user
  // can start typing immediately.
  useFocusTrap(open, sheetRef, {
    onEscape: onClose,
    initialFocus: () =>
      sheetRef.current?.querySelector<HTMLElement>(
        'textarea, input[type="text"]'
      ) ?? null,
  });

  useEffect(() => {
    if (!sheetRef.current) return;
    if (isOpen) {
      sheetRef.current.removeAttribute("inert");
    } else {
      sheetRef.current.setAttribute("inert", "");
    }
  }, [isOpen]);

  // Drag handling for the sheet handle
  const handleDragStart = useCallback((clientY: number) => {
    setDragStartY(clientY);
  }, []);

  const handleDragEnd = useCallback(
    (clientY: number) => {
      if (dragStartY === null) return;
      const delta = dragStartY - clientY;

      if (state === "half" && delta > 60) {
        setState("full");
      } else if (state === "full" && delta < -60) {
        setState("half");
      } else if (state === "half" && delta < -80) {
        onClose();
      }
      setDragStartY(null);
    },
    [dragStartY, onClose, state]
  );

  const sheetHeight = state === "full" ? "h-[92vh]" : "h-[55vh]";
  const mobileSheetFrame = isOpen ? `${sheetHeight} translate-y-0` : "h-0 translate-y-full";
  const desktopSheetFrame = isOpen
    ? state === "full"
      ? "lg:h-[calc(100vh-2rem)] lg:w-[28rem] lg:translate-y-0 lg:opacity-100"
      : "lg:h-[min(720px,calc(100vh-4rem))] lg:w-[26rem] lg:translate-y-0 lg:opacity-100"
    : "lg:h-0 lg:w-[26rem] lg:translate-y-6 lg:opacity-0 lg:pointer-events-none";

  return (
    <>
      {/* Floating chat button — hidden when this sheet is open OR
          when another overlay (Settings, NewDecision) is on top, so
          mobile users don't see the chat button visually layered
          over an active sheet. */}
      {!isOpen && !triggerHidden && (
        <button
          onClick={onOpen}
          className="fixed bottom-[72px] right-4 z-[55] w-14 h-14 rounded-full
                     bg-forest text-white shadow-lg hover:bg-forest-med
                     flex items-center justify-center
                     transition-all duration-200 hover:scale-105 active:scale-95
                     lg:hidden"
          aria-label="Open chat"
          data-testid="chat-button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          data-testid="chat-sheet-backdrop"
          className="fixed inset-0 z-[58] bg-black/30 backdrop-blur-[2px] transition-opacity lg:left-60 lg:bg-black/10 lg:backdrop-blur-0"
          onClick={onClose}
        />
      )}

      {/* Bottom sheet */}
      <div
        ref={sheetRef}
        data-testid={isOpen ? "chat-sheet" : undefined}
        role={isOpen ? "dialog" : undefined}
        aria-modal={isOpen ? "true" : undefined}
        aria-labelledby={isOpen ? titleId : undefined}
        aria-hidden={isOpen ? undefined : true}
        className={`fixed bottom-0 left-0 right-0 z-[59] bg-card rounded-t-2xl shadow-2xl
                    transition-all duration-300 ease-out overflow-hidden
                    ${mobileSheetFrame}
                    lg:left-auto lg:right-6 lg:bottom-6 lg:rounded-[28px] lg:border lg:border-border/80
                    ${desktopSheetFrame}`}
      >
        {isOpen && (
          <>
            {/* Drag handle */}
            <div
              className="flex justify-center items-center py-2.5 cursor-grab active:cursor-grabbing touch-none"
              onMouseDown={(e) => handleDragStart(e.clientY)}
              onMouseUp={(e) => handleDragEnd(e.clientY)}
              onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
              onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientY)}
            >
              <div className="w-10 h-1 rounded-full bg-border" />
            </div>

            {/* Header bar */}
            <div className="flex items-center justify-between px-4 pb-2">
              <h3 id={titleId} className="text-sm font-semibold text-text">Chat</h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleFull}
                  className="p-1.5 rounded-lg hover:bg-secondary/50 text-muted"
                  aria-label={state === "full" ? "Minimize" : "Maximize"}
                >
                  {state === "full" ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-secondary/50 text-muted"
                  aria-label="Close chat"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Chat content — reuse ChatScreen */}
            <div className="flex-1 overflow-hidden" style={{ height: "calc(100% - 56px)" }}>
              <ChatScreen
                screenContext={screenContext}
                initialMessage={initialMessage}
                onInitialMessageConsumed={onInitialMessageConsumed}
                focusedGoalId={focusedGoalId}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
